import { z } from "zod";
import { ErrorCode } from "../errors.js";
import { Id, IsoDate, IsoTimestamp } from "../primitives.js";
import { Query } from "./query.js";

/**
 * Asking a question — docs/08 §8.3–8.7.
 *
 * The model's only actionable output is a **schema-validated tool argument**,
 * and the only schema it can produce is `Query`, which has no household field.
 * Everything below describes what comes back out: prose, and a record of what
 * was looked up to produce it. None of it is written by the model except the
 * prose itself, and none of it is executed.
 */

/**
 * The read-only tools, named on the wire so the lookup panel can label them.
 *
 * There is no write tool — not gated, not permissioned, **absent**. The list
 * being a closed enum here is part of that: an event naming a tool this does
 * not know about fails the client's parse rather than rendering.
 */
export const AskTool = z.enum([
  "describe_data",
  "aggregate",
  "list_transactions",
  "get_budget_status",
  "get_plan",
]);

/**
 * One entry in the "what I looked up" panel.
 *
 * `description` is rendered from the DSL object by `core/query/describe.ts`,
 * never written by the model — so it cannot misdescribe what ran. `query` is
 * the object itself, so the panel can offer the drill-through that makes the
 * figure checkable in one click.
 */
export const Lookup = z.object({
  /** The tool-use id, stable within a conversation. */
  id: z.string(),
  tool: AskTool,
  description: z.string(),
  /** Present for the two tools that take a DSL query; null for the others. */
  query: Query.nullable(),
  /** How many rows came back, so "nothing matched" is visible rather than implied. */
  rowCount: z.number().int(),
  /** A tool that failed still appears: a lookup hidden because it broke is a lie by omission. */
  failed: z.boolean(),
});

/**
 * The result of the numeric grounding check (docs/08 §8.4 point 3).
 *
 * Every number in the prose is extracted and matched against a figure some
 * tool actually returned. Unmatched numbers are flagged in the UI rather than
 * silently trusted — cheap, and it catches the only failure that matters: a
 * plausible figure from nowhere.
 */
export const Grounding = z.object({
  checked: z.number().int(),
  /** The unmatched figures, as they were written, so the UI can name them. */
  unverified: z.array(z.string()),
});

export const AskUsage = z.object({
  tokensIn: z.number().int(),
  tokensOut: z.number().int(),
  tokensCached: z.number().int(),
});

/**
 * The SSE stream, as a discriminated union.
 *
 * The client parses every event with this schema, so a shape the server could
 * not have produced fails at the boundary rather than three components away.
 * Text arrives as it is generated and lookups appear as they happen, so the
 * user watches the work rather than a spinner (docs/05 §5.7).
 */
export const AskEvent = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("started"),
    conversationId: Id,
    /** The assistant message's id, allocated up front so the client can key on it. */
    messageId: Id,
    title: z.string(),
  }),
  z.object({ type: z.literal("lookup"), lookup: Lookup }),
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({
    type: z.literal("done"),
    messageId: Id,
    grounding: Grounding,
    usage: AskUsage,
  }),
  /**
   * A failure mid-stream. The HTTP status is already 200 by the time anything
   * can go wrong, so the error travels in the stream — including the model's
   * own refusal, which the API also returns as a 200.
   */
  z.object({ type: z.literal("error"), code: ErrorCode, message: z.string() }),
]);

export const AskRequest = z.strictObject({
  /** Absent starts a new conversation. Unknown, or another household's, is a 404. */
  conversationId: Id.optional(),
  question: z.string().trim().min(1, "Ask a question first").max(2000),
});

export const ConversationSummary = z.object({
  id: Id,
  title: z.string(),
  createdAt: IsoTimestamp,
  lastMessageAt: IsoTimestamp,
});

export const ConversationList = z.object({ data: z.array(ConversationSummary) });

/**
 * One exchange, as a person reads it: what was asked, what came back, and what
 * was looked up in between.
 *
 * Stored messages are the API's own content blocks — tool calls included,
 * because a follow-up replays them verbatim and a `tool_use` without its
 * `tool_result` is a malformed request. This is the display projection of
 * those rows, derived on read rather than stored twice.
 */
export const AskTurn = z.object({
  id: Id,
  question: z.string(),
  answer: z.string(),
  lookups: z.array(Lookup),
  grounding: Grounding,
  askedAt: IsoTimestamp,
});

export const ConversationDetail = ConversationSummary.extend({
  turns: z.array(AskTurn),
});

/**
 * What the household has spent on questions, shown in settings.
 *
 * Hidden cost is how you get a surprise (docs/08 §8.6), and this is a feature
 * for the person paying rather than an operational metric.
 */
export const AiUsageDay = z.object({
  day: IsoDate,
  tokensIn: z.number().int(),
  tokensOut: z.number().int(),
  costMicros: z.number().int(),
  requests: z.number().int(),
});

export const AiUsageSummary = z.object({
  today: AiUsageDay,
  /** The per-household daily token cap in force. */
  dailyTokenCap: z.number().int(),
  /** Newest first, up to a month. */
  recent: z.array(AiUsageDay),
});

export type AskTool = z.infer<typeof AskTool>;
export type Lookup = z.infer<typeof Lookup>;
export type Grounding = z.infer<typeof Grounding>;
export type AskUsage = z.infer<typeof AskUsage>;
export type AskEvent = z.infer<typeof AskEvent>;
export type AskRequest = z.infer<typeof AskRequest>;
export type ConversationSummary = z.infer<typeof ConversationSummary>;
export type ConversationList = z.infer<typeof ConversationList>;
export type AskTurn = z.infer<typeof AskTurn>;
export type ConversationDetail = z.infer<typeof ConversationDetail>;
export type AiUsageDay = z.infer<typeof AiUsageDay>;
export type AiUsageSummary = z.infer<typeof AiUsageSummary>;
